import { useEffect, useRef } from "react";
import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import {
  peekResolvedPath,
  resolveRoutePath,
  RouteRenderMode,
  TimedPathPoint,
} from "./resolveRoutePath";

// Geometry for the stacked comparison views, fed to routeOverlayLines.
//
// A separate pass rather than widening useResolvedRoutePaths: the line that
// carries pins, replay and hit handling keeps the pipeline it had, and
// stacking is additive on top. resolveRoutePath caches per (mode, trip), so
// re-ticking a view costs nothing.

// smaller than the primary pass's batch: these run alongside it, and
// DirectionsService starts returning OVER_QUERY_LIMIT when several views'
// worth of trips are pushed at once
const RESOLVE_BATCH_SIZE = 3;

export type OverlayTripToResolve = {
  tripKey: string;
  tripIndex: number;
  points: VehicleMapDataPoint[];
};

export const useRouteOverlayPaths = ({
  trips,
  modes,
  onResolved,
}: {
  trips: OverlayTripToResolve[];
  modes: RouteRenderMode[];
  onResolved: (
    tripKey: string,
    mode: RouteRenderMode,
    path: TimedPathPoint[]
  ) => void;
}) => {
  const runIdRef = useRef(0);

  const onResolvedRef = useRef(onResolved);
  onResolvedRef.current = onResolved;

  // identity-independent, so an unrelated re-render doesn't restart the work
  const tripsKey = trips
    .map((trip) => `${trip.tripKey}:${trip.points.length}`)
    .join(",");
  const modesKey = modes.join(",");

  useEffect(() => {
    const runId = ++runIdRef.current;
    if (trips.length === 0 || modes.length === 0) return;

    // whatever is already known goes up immediately, so a view that was
    // just the primary keeps its line instead of blinking out and
    // re-resolving as a comparison line
    modes.forEach((mode) =>
      trips.forEach((trip) => {
        const cached = peekResolvedPath(mode, trip.tripKey, trip.points);
        if (!cached) return;
        onResolvedRef.current(trip.tripKey, mode, cached);
      })
    );

    (async () => {
      // view by view, so the first comparison line lands as a whole rather
      // than every view filling in a third of the way at once
      for (const mode of modes) {
        for (let start = 0; start < trips.length; start += RESOLVE_BATCH_SIZE) {
          if (runId !== runIdRef.current) return;
          const batch = trips.slice(start, start + RESOLVE_BATCH_SIZE);
          await Promise.all(
            batch.map(async (trip) => {
              const path = await resolveRoutePath(
                mode,
                trip.tripKey,
                trip.tripIndex,
                trip.points
              );
              if (runId !== runIdRef.current) return;
              onResolvedRef.current(trip.tripKey, mode, path);
            })
          );
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripsKey, modesKey]);
};
